import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Typography,
  MenuItem,
} from '@mui/material';
import {
  Assignment as LogIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import api from '../../../api';
import { showError } from '../../../utils/sweetAlertConfig';

const PRIMARY = '#29204C';
const SUCCESS = '#68B977';

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, filterType, searchTerm]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {
        per_page: rowsPerPage,
        page: page + 1,
        user_type: filterType !== 'all' ? filterType : undefined,
        search: searchTerm || undefined,
      };

      const response = await api.get('/logs', { params });

      if (response.data && response.data.data) {
        setLogs(response.data.data);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      showError('Failed to load logs. Please try again.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getLogTypeChip = (severity) => {
    const typeConfig = {
      info: { label: 'Info', color: '#1976d2', icon: <InfoIcon fontSize="small" /> },
      success: { label: 'Success', color: SUCCESS, icon: <SuccessIcon fontSize="small" /> },
      warning: { label: 'Warning', color: '#ed6c02', icon: <WarningIcon fontSize="small" /> },
      error: { label: 'Error', color: '#d32f2f', icon: <ErrorIcon fontSize="small" /> },
      critical: { label: 'Critical', color: '#d32f2f', icon: <ErrorIcon fontSize="small" /> },
    };

    const config = typeConfig[severity?.toLowerCase()] || typeConfig.info;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          bgcolor: `${config.color}15`,
          color: config.color,
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: config.color,
          },
        }}
      />
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const columns = [
    {
      field: 'timestamp',
      label: 'Timestamp',
      width: 180,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(row.timestamp || row.formatted_date)}
        </Typography>
      ),
    },
    {
      field: 'severity',
      label: 'Severity',
      width: 120,
      align: 'center',
      render: (row) => getLogTypeChip(row.severity),
    },
    {
      field: 'user',
      label: 'User',
      minWidth: 180,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: PRIMARY, width: 32, height: 32 }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.user_name || 'System'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.user_role || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'action',
      label: 'Action',
      minWidth: 200,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.action}
        </Typography>
      ),
    },
    {
      field: 'description',
      label: 'Description',
      minWidth: 300,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.description || 'No description'}
        </Typography>
      ),
    },
    {
      field: 'ip_address',
      label: 'IP Address',
      width: 140,
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.ip_address || 'N/A'}
        </Typography>
      ),
    },
  ];

  // Remove client-side filtering since we're now using server-side pagination
  const paginatedLogs = logs;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Logs & Notes"
        subtitle="View system activity logs and audit trail"
        icon={LogIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Logs & Notes' },
        ]}
      />

      {/* Search and Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by user, action, description, or IP address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
            },
          }}
        />
        <TextField
          select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
            },
          }}
        >
          <MenuItem value="all">All Users</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="candidate">Candidate</MenuItem>
          <MenuItem value="system">System</MenuItem>
        </TextField>
      </Box>

      {/* Logs Table */}
      <ModernTable
        columns={columns}
        data={paginatedLogs}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={logs.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage="No logs found. System activity will be recorded here."
        showActions={false}
      />
    </Container>
  );
}
